/**
 * FERP Portal - Kanban Renderer
 *
 * DRR-178: Column-based board view for primitives grouped by a categorical field
 * (status, safety level, priority, etc.). Pure Mantine — no external kanban library.
 *
 * Follows the same fieldAdapter pattern as EntityCardGrid for DRY field extraction.
 *
 * Usage:
 * ```tsx
 * <KanbanRenderer
 *   items={tasks}
 *   columnField="status"
 *   fieldAdapter={primitiveFieldAdapter('task', resolveActorName)}
 *   onItemClick={handleView}
 * />
 * ```
 */

import { useMemo } from 'react';
import {
  Group,
  Stack,
  Card,
  Text,
  Badge,
  ScrollArea,
  Paper,
  ThemeIcon,
} from '@mantine/core';
import { IconCircleDot } from '@tabler/icons-react';

// Status → color mapping (matches STATUS_BADGE_MAP in tabulatorConfig)
const COLUMN_COLORS: Record<string, string> = {
  PENDING: 'yellow',
  IN_PROGRESS: 'blue',
  COMPLETED: 'green',
  FAILED: 'red',
  CANCELLED: 'gray',
  COMPENSATED: 'grape',
  ACTIVE: 'teal',
  INACTIVE: 'gray',
  DRAFT: 'gray',
  PUBLISHED: 'green',
  ARCHIVED: 'dimmed',
  CRITICAL: 'red',
  HIGH: 'orange',
  MEDIUM: 'yellow',
  LOW: 'blue',
};

export interface KanbanFieldConfig {
  name: string;
  subtitle?: string;
  status?: string;
  safetyLevel?: string;
  accentColor?: string;
}

interface KanbanRendererProps<T> {
  items: T[];
  /** Field to group items into columns */
  columnField: string;
  /** Optional column ordering (values not in this list appear at the end) */
  columnOrder?: string[];
  /** Maps an item to display fields */
  fieldAdapter: (item: T) => KanbanFieldConfig;
  /** Callback when an item card is clicked */
  onItemClick?: (item: T) => void;
  /** Extract unique ID from item */
  getId?: (item: T) => string;
  /** Max height for the kanban container */
  maxHeight?: string;
}

export function KanbanRenderer<T>({
  items,
  columnField,
  columnOrder,
  fieldAdapter,
  onItemClick,
  getId = (item: T) => (item as Record<string, unknown>).id as string,
  maxHeight = '600px',
}: KanbanRendererProps<T>) {
  // Group items by column field value
  const columns = useMemo(() => {
    const grouped = new Map<string, T[]>();

    for (const item of items) {
      const value = String(
        (item as Record<string, unknown>)[columnField] ?? '(none)'
      );
      if (!grouped.has(value)) {
        grouped.set(value, []);
      }
      grouped.get(value)!.push(item);
    }

    // Sort columns: ordered first, then remaining alphabetically
    const allKeys = Array.from(grouped.keys());
    const orderedKeys = columnOrder
      ? [
          ...columnOrder.filter((k) => grouped.has(k)),
          ...allKeys.filter((k) => !columnOrder.includes(k)).sort(),
        ]
      : allKeys.sort();

    return orderedKeys.map((key) => ({
      key,
      items: grouped.get(key) ?? [],
    }));
  }, [items, columnField, columnOrder]);

  if (items.length === 0) {
    return (
      <Paper p="xl" ta="center" c="dimmed">
        <Text>No items to display</Text>
      </Paper>
    );
  }

  return (
    <ScrollArea style={{ maxHeight }} offsetScrollbars data-testid="kanban-board">
      <Group align="flex-start" gap="md" wrap="nowrap" style={{ minWidth: columns.length * 260 }}>
        {columns.map((col) => (
          <Paper
            key={col.key}
            withBorder
            p="sm"
            radius="md"
            data-testid={`kanban-column-${col.key}`}
            style={{ minWidth: 240, maxWidth: 300, flex: '1 1 240px' }}
          >
            {/* Column header */}
            <Group gap="xs" mb="sm">
              <ThemeIcon
                size="xs"
                variant="filled"
                color={COLUMN_COLORS[col.key] ?? 'gray'}
                radius="xl"
              >
                <IconCircleDot size={10} />
              </ThemeIcon>
              <Text fw={600} size="sm">
                {col.key}
              </Text>
              <Badge size="sm" variant="light" color="gray" ml="auto">
                {col.items.length}
              </Badge>
            </Group>

            {/* Column items */}
            <Stack gap="xs">
              {col.items.map((item) => {
                const fields = fieldAdapter(item);
                return (
                  <Card
                    key={getId(item)}
                    withBorder
                    padding="xs"
                    radius="sm"
                    data-testid={`kanban-card-${getId(item)}`}
                    style={{
                      cursor: onItemClick ? 'pointer' : undefined,
                      borderLeft: fields.accentColor
                        ? `3px solid ${fields.accentColor}`
                        : undefined,
                    }}
                    onClick={() => onItemClick?.(item)}
                  >
                    <Text size="sm" fw={500} lineClamp={2}>
                      {fields.name}
                    </Text>
                    {fields.subtitle && (
                      <Text size="xs" c="dimmed" lineClamp={1} mt={2}>
                        {fields.subtitle}
                      </Text>
                    )}
                    {fields.safetyLevel && fields.safetyLevel !== 'LOW' && (
                      <Badge
                        size="xs"
                        variant="light"
                        color={COLUMN_COLORS[fields.safetyLevel] ?? 'gray'}
                        mt={4}
                      >
                        {fields.safetyLevel}
                      </Badge>
                    )}
                  </Card>
                );
              })}
            </Stack>
          </Paper>
        ))}
      </Group>
    </ScrollArea>
  );
}
